import { useEffect, useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * FavoriteButton — save/unsave a property (♡ / ♥).
 * Self-contained: handles auth-gating (guest -> toast + login) and the API call.
 * Parent may pass `initialFavorited` and listen via `onToggled(propertyId, isFav)`.
 */
export default function FavoriteButton({
  propertyId,
  initialFavorited = false,
  onToggled,
  variant = "overlay", // "overlay" (card) | "detail"
  className = "",
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fav, setFav] = useState(initialFavorited);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFav(initialFavorited);
  }, [initialFavorited]);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;

    if (!user) {
      toast.info("Silakan login untuk menyimpan favorit", {
        action: { label: "Login", onClick: () => navigate("/login") },
      });
      return;
    }
    if (user.role !== "customer") {
      toast.error("Hanya customer yang dapat menyimpan favorit.");
      return;
    }

    const next = !fav;
    setFav(next); // optimistic
    setBusy(true);
    try {
      if (next) {
        await apiClient.post("/favorites", { property_id: propertyId });
        toast.success("Ditambahkan ke Favorit Saya");
      } else {
        await apiClient.delete(`/favorites/${propertyId}`);
        toast.success("Dihapus dari favorit");
      }
      onToggled?.(propertyId, next);
    } catch (err) {
      setFav(!next); // revert
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (variant === "detail") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-pressed={fav}
        data-testid={`favorite-toggle-${propertyId}`}
        data-favorited={fav}
        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold border transition-colors disabled:opacity-60 ${
          fav
            ? "bg-rose-50 border-rose-200 text-rose-600"
            : "bg-card border-border text-foreground hover:bg-secondary/60"
        } ${className}`}
      >
        {busy ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Heart size={16} className={fav ? "fill-rose-500 text-rose-500" : ""} />
        )}
        {fav ? "Favorited" : "Favorite"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={fav}
      data-testid={`favorite-toggle-${propertyId}`}
      data-favorited={fav}
      title={fav ? "Hapus dari favorit" : "Simpan ke favorit"}
      className={`grid place-items-center w-9 h-9 rounded-full bg-white/90 backdrop-blur shadow-sm hover:scale-110 active:scale-95 transition-transform disabled:opacity-60 ${className}`}
    >
      {busy ? (
        <Loader2 size={16} className="animate-spin text-muted-foreground" />
      ) : (
        <Heart size={17} className={fav ? "fill-rose-500 text-rose-500" : "text-slate-600"} />
      )}
    </button>
  );
}
